import { cn } from '@invent/wl-ui-kit-next';
import * as React from 'react';

import { JOURNEYS, type JourneyCode } from './fixtures';
import type { IRunState } from './flow';
import { ProvenanceBadge } from './shared';

/**
 * Every branch on one page, with the path the current run took drawn over it.
 *
 * The clickthrough answers "what does this look like"; the map answers "what else can
 * happen", which is the question a reviewer of a *process* actually has. It is a decision
 * table rather than a boxes-and-arrows drawing on purpose: the branches here fan out four
 * and five ways from a single answer, and an arrow diagram of that is unreadable at the
 * width a browser has.
 */

interface IBranch {
  /** What produced it. */
  when: string;
  /** What the flow does next. */
  then: string;
  /** Highlighted when the current run matches. */
  active?: boolean;
  tone?: 'plain' | 'refusal' | 'redirect';
}

function BranchList({ branches }: { branches: IBranch[] }) {
  return (
    <ul className="grid gap-1.5">
      {branches.map((branch) => (
        <li
          key={branch.when}
          className={cn(
            'grid gap-1 rounded-md border px-3 py-2 text-xs sm:grid-cols-[minmax(0,15rem)_1fr] sm:gap-3',
            branch.active
              ? 'border-[var(--color-sapphire)] bg-[var(--color-slight-blue)]/50'
              : 'bg-card',
            branch.tone === 'refusal' && !branch.active && 'border-dashed'
          )}
        >
          <span className="font-medium text-[var(--color-royal-blue)]">{branch.when}</span>
          <span
            className={cn(
              'text-muted-foreground',
              branch.tone === 'refusal' && 'text-[var(--color-deep-magenta)]',
              branch.tone === 'redirect' && 'text-[var(--color-teal)]'
            )}
          >
            {branch.then}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Stage({
  title,
  subtitle,
  children
}: {
  title: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="grid gap-2">
      <header>
        <h3 className="text-sm font-medium text-[var(--color-royal-blue)]">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </header>
      {children}
    </section>
  );
}

/**
 * The four journeys that open a registration run — the front door and the three migrated
 * journeys do not, so a lookup result they never had must not light a row up.
 */
const REGISTERING: JourneyCode[] = ['fa', 'fsp', 'ria', 'teams'];

/** The question codes the current run has answered, for the tree's rows to light up by. */
function answered(state: IRunState, code: string, value?: string | number): boolean {
  return state.given.some(
    (answer) => answer.question.code === code && (value === undefined || answer.value === value)
  );
}

export function FlowMap({ state }: { state: IRunState }) {
  const registering = REGISTERING.includes(state.journey);
  const team = state.journey === 'teams';
  const ria = state.journey === 'ria';

  return (
    <div className="mx-auto grid max-w-4xl gap-8 px-6 py-8">
      <Stage
        title="1 · Who is at the door"
        subtitle={
          <>
            One door — the sign-in screen — and a question behind it: which registration is
            this? FA and TEAMS both anchor on a CRD, so it cannot be inferred from the number.
            The product asks it at <code>/registration</code> and offers four, and asks it again
            inside an account at <code>/portal/surveys/add</code>. Four of the journeys below create an
            account; three belong to people who already have one.
          </>
        }
      >
        <ul className="grid gap-1.5 sm:grid-cols-2">
          {JOURNEYS.map((entry) => (
            <li
              key={entry.code}
              className={cn(
                'grid gap-1 rounded-md border px-3 py-2 text-xs',
                entry.code === state.journey
                  ? 'border-[var(--color-sapphire)] bg-[var(--color-slight-blue)]/50'
                  : 'bg-card'
              )}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="font-medium text-[var(--color-royal-blue)]">{entry.label}</span>
                <ProvenanceBadge provenance={entry.provenance} />
              </span>
              <span className="text-muted-foreground">{entry.who}</span>
            </li>
          ))}
        </ul>
      </Stage>

      <Stage
        title="2 · The anchor, and the two questions asked of it"
        subtitle={
          <>
            FA on a CRD, FSP on an NPN or a licence, RIA on a firm SEC number, a team on its
            lead advisor’s CRD. The registry is asked whether the number is real; our own data
            is asked who already holds it.
          </>
        }
      >
        <BranchList
          branches={[
            {
              when: 'Registry ok, number free',
              then:
                '→ confirm_identity, naming the person or firm the registry named. Stored ' +
                'verified at completion, on a yes to that name.',
              active: registering && state.lookup === 'ok' && !state.linkTo
            },
            {
              when: 'Registry error, number free',
              then:
                '→ complete, with a notice that the number stays unconfirmed — not a prompt; a ' +
                'team to its route, every question from the SEC one. Stored unverified. Every ' +
                'FSP lookup today, and FA’s or a team’s when FINRA cannot be asked.',
              active: registering && state.lookup === 'error' && !state.linkTo
            },
            {
              when: 'Registry not_found, first attempt · FA, FSP, a team',
              then: '→ back to anchor with a retry — a typo is likelier than no number at all.',
              active: registering && !ria && state.lookup === 'not_found' && state.attempts < 2
            },
            {
              when: 'Registry not_found, second attempt · FA, FSP, a team',
              then:
                '→ complete — a team to its route, at the trust company question — and no ' +
                'identifier is stored at all.',
              active:
                registering && !ria && state.lookup === 'not_found' && state.attempts >= 2
            },
            {
              when: 'Registry not_found, any attempt · RIA',
              then:
                '→ back to anchor with the notice “Only SEC registered firms are eligible for ' +
                'ranking.” and the RIA-vs-Teams criteria — another number, or “Register as a ' +
                'team instead”.',
              active: registering && ria && state.lookup === 'not_found' && !state.redirected
            },
            {
              when: '“Register as a team instead” · RIA',
              then:
                '→ redirected to the team survey (/redirection): the run ends, nothing is ' +
                'created, and “Register the team” starts a new run.',
              tone: 'redirect',
              active: registering && ria && state.redirected
            },
            {
              when: '“Our lead advisor has no CRD number” · a team',
              then: '→ the route, at the trust company question, with nothing looked up (/no-anchor).',
              active: team && state.lookup === null && state.route !== null
            },
            {
              when: 'Signed in, a number already on the account',
              then:
                '409 identifier-already-yours, naming the participant — the one refusal that ' +
                'names anybody, since the holder is the caller. The run stays at the anchor.',
              tone: 'refusal'
            },
            {
              when: 'Number held, holder has an account',
              then:
                'Run abandoned · identifier_unavailable. The body names the value, never the ' +
                'holder. For a team: another team holds the lead CRD.',
              tone: 'refusal',
              active: state.refusal?.reason === 'identifier_unavailable'
            },
            {
              when: 'Number held, nobody can log in as them',
              then:
                '→ confirm_identity naming them, whatever the registry said, and completion ' +
                'LINKS the new account to that participant. 258 of these.',
              active: state.linkTo !== null
            },
            {
              when: '“That is not me”, or “Enter a different number” at the route or completion',
              then:
                '→ back to anchor through one reset — /identity or /revision — everything the ' +
                'number established dropped, attempt count kept.',
              active: false
            },
            {
              when: 'Back, on the anchor',
              then: '→ the chooser, and the run is abandoned (/abandonment, 204).',
              active: false
            }
          ]}
        />
      </Stage>

      <Stage
        title="3 · A team’s route"
        subtitle={
          <>
            survey-api’s, read once the lead is confirmed: whether a firm FINRA BrokerCheck or the
            SEC’s IAPD lists the lead at is on the wirehouse list (staff own it), then SR-416’s
            questions — TEAM-RG-001’s trust company prompt and TEAM-RG-004…006. Every entry asks a
            question first; no firm routes a team alone. The only part of the process with more
            than one destination.
          </>
        }
      >
        <BranchList
          branches={[
            {
              when: 'A firm on the wirehouse list',
              then:
                '→ “Is your team part of a private wealth channel within your firm?” Either ' +
                'answer is the Wires Team Survey; the answer is kept for TEAM-RG-004.',
              active: answered(state, 'private_wealth')
            },
            {
              when: 'No firm at all — none listed, no match, no CRD',
              then:
                '→ “Are you affiliated with a trust company?” Yes: Independent Practices. No: ' +
                'submitting for an RIA?',
              active: answered(state, 'trust_company')
            },
            {
              when: 'Any other firm, or FINRA unreachable',
              then:
                '→ “Are you submitting this survey on behalf of an RIA?” — under the firm’s ' +
                'register label, or FINRA’s name for it. An unmapped FINRA firm is recorded for ' +
                'staff.',
              active: answered(state, 'ria_submission')
            },
            {
              when: 'Submitting for an RIA · yes',
              then:
                '→ redirected to the RIA firm survey: the run ends, nothing is created, and ' +
                '“Register the firm” starts a new run.',
              tone: 'redirect',
              active: team && state.step === 'redirected'
            },
            {
              when: 'Submitting for an RIA · no',
              then:
                '→ the practice’s structure: Not Independent is the Wires Team Survey; a ' +
                'Corporate RIA umbrella, an IBD, both, or a trust company is Independent ' +
                'Practices.',
              active: answered(state, 'structure')
            },
            {
              when: 'A team on file nobody signs in for',
              then: '→ confirmed, linked, and no route: it keeps the routing it has.',
              active: team && state.linkTo !== null
            }
          ]}
        />
      </Stage>

      <Stage
        title="4 · The completion, and what it refuses"
        subtitle={
          'One transaction: account, participant, link, identifier — and a new team’s ' +
          'routing. No response.'
        }
      >
        <BranchList
          branches={[
            {
              when: 'Password fails the policy',
              then: 'The rules that failed come back, never the password. The run stays open.',
              active: state.passwordFailures.length > 0
            },
            {
              when: 'Address already an account',
              then:
                'address_unavailable · duplicate_address. The run stays in progress — an ' +
                'address is something a registrant can change.',
              tone: 'refusal',
              active: state.refusal?.reason === 'address_unavailable' && !state.refusal.deadEnd
            },
            {
              when: 'Address belongs to somebody else’s account',
              then:
                'The same refusal to the registrant, a different event to staff. 54 people: a ' +
                'different address gets through, and for somebody with only this one the way ' +
                'out is a link staff make on their participant card (block 6).',
              tone: 'refusal',
              active: state.refusal?.deadEnd === true
            },
            {
              when: 'The number was claimed in the meantime',
              then:
                'identifier_unavailable again, this time inside the transaction. Nothing is ' +
                'recorded and the run reads as stuck.',
              tone: 'refusal'
            },
            {
              when: 'A tab resumed on the wrong step',
              then: '409 naming the step the run is actually on, and its status.',
              tone: 'refusal',
              active: state.refusal?.reason === 'registration_out_of_step'
            },
            {
              when: 'A run left for seven days',
              then:
                'The sweep abandons it, and the next read answers 409 with status abandoned — ' +
                'the same answer a cancel from another tab gets. A fresh start is the way on.',
              tone: 'refusal',
              active: state.step === 'run_closed'
            }
          ]}
        />
      </Stage>

      <Stage
        title="5 · Where the account lands"
        subtitle={
          <>
            Which survey is offered to start, and to whom — nothing is started for anybody. A
            cycle is listed for a participant that has answered that survey before, holds a
            number it anchors on, or completed a registration for it that was not redirected.
            What it has no answer for at all is the last row.
          </>
        }
      >
        <BranchList
          branches={[
            {
              when: 'Person with a CRD',
              then: 'Top Financial Advisors, and nothing else.',
              active: state.step === 'landed' && state.destination.surveyCode === 'FA'
            },
            {
              when: 'Person with an NPN or a licence',
              then:
                'Top Financial Security Professionals. A CRD entered later branches that ' +
                'survey; it does not add another.',
              active: state.step === 'landed' && state.destination.surveyCode === 'FSP'
            },
            {
              when: 'Firm participant on an SEC number',
              then: 'Top RIA Firms. The participant is the firm, not the person who registered it.',
              active: state.step === 'landed' && state.destination.surveyCode === 'RIA_FIRM'
            },
            {
              when: 'Team',
              then:
                'Top Wealth Advisor Teams, started on the path its routing names — the create ' +
                'sends no pathCode. Routing unknown: its own questions first. Routed to the ' +
                'RIA firm survey: “Register the firm”.',
              active: state.step === 'landed' && state.destination.surveyCode === 'TEAMS'
            },
            {
              when: 'An account acting for two participants',
              then:
                'One entry per participant, and each cycle names which of them it is for — ' +
                'eligibleParticipants. 11 531 migrated accounts hold two, always org + person.'
            },
            {
              when: 'Answered the survey in an earlier cycle',
              then:
                'Entitled to it, every non-planned cycle included — the historical ones are ' +
                'what the past-cycles list is made of.'
            },
            {
              when: 'The cycle is closed',
              then:
                'Listed as historical, and a start in it is refused 409 cycle-closed. Writes ' +
                'to a draft the close caught are refused the same way.',
              tone: 'refusal'
            },
            {
              when: 'Neither a response, an anchoring number nor a registration',
              then:
                'Nothing at all: GET /my/cycles answers []. 10 586 of the 55 056 migrated ' +
                'accounts, and a redirected firm. The way on is “Add a survey”.',
              tone: 'refusal',
              active: state.step === 'nothing_offered'
            }
          ]}
        />
      </Stage>
    </div>
  );
}
